import type { ComponentType } from 'react';

import type { Condition } from '../conditions';

// ============================================================================
// Generic Route Types
// ============================================================================

/**
 * @deprecated Use Condition<TContext> from @connected/core instead.
 * Kept for backwards compatibility during migration.
 */
export type RouteCondition<TContext> = (context: TContext) => boolean;

/**
 * Represents a clickable route (Page or Tab).
 * Distinctive feature: It MUST have a `path`.
 *
 * The condition field now supports both:
 * - Named conditions: 'canAccessQuotation'
 * - Inline functions: (ctx) => ctx.someFlag
 *
 * The second type parameter allows apps to constrain condition names to a union
 * of valid names, enabling compile-time type checking for condition strings.
 */
export type BaseLinkRoute<TContext, TConditionName extends string = string> = {
    name: string;
    path: string;
    icon?: ComponentType;
    /** Condition for visibility - can be a named condition string or inline function */
    condition?: Condition<TContext, TConditionName>;
    /** Path to navigate to when exiting this route (e.g., last tab in a sequence) */
    exitTo?: string;
    /** Nested links (e.g., Tabs) */
    routes?: BaseLinkRoute<TContext, TConditionName>[];
};

/**
 * Represents a container/category.
 * Distinctive feature: It MUST NOT have a `path`.
 *
 * The second type parameter allows apps to constrain condition names to a union
 * of valid names, enabling compile-time type checking for condition strings.
 */
export type BaseGroupRoute<TContext, TConditionName extends string = string> = {
    name: string;
    /** Groups must contain children */
    routes: (BaseGroupRoute<TContext, TConditionName> | BaseLinkRoute<TContext, TConditionName>)[];
    /** Condition for visibility - can be a named condition string or inline function */
    condition?: Condition<TContext, TConditionName>;
    // Explicitly ban link-specific properties to ensure strict union narrowing
    path?: never;
    icon?: never;
    exitTo?: never;
};

/**
 * Union type for any navigation node.
 *
 * The second type parameter allows apps to constrain condition names to a union
 * of valid names, enabling compile-time type checking for condition strings.
 */
export type BaseRoute<TContext, TConditionName extends string = string> =
    | BaseGroupRoute<TContext, TConditionName>
    | BaseLinkRoute<TContext, TConditionName>;

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if a route is a LinkRoute (has a path).
 */
export function isLinkRoute<TContext, TConditionName extends string = string>(
    route: BaseRoute<TContext, TConditionName>,
): route is BaseLinkRoute<TContext, TConditionName> {
    return 'path' in route && typeof route.path === 'string';
}

/**
 * Type guard to check if a route is a GroupRoute (no path).
 */
export function isGroupRoute<TContext, TConditionName extends string = string>(
    route: BaseRoute<TContext, TConditionName>,
): route is BaseGroupRoute<TContext, TConditionName> {
    return !('path' in route) || route.path === undefined;
}

// ============================================================================
// Result Types
// ============================================================================

/**
 * Result type for prev/next navigation.
 */
export type PrevNextResult<TContext, TConditionName extends string = string> = {
    prev: BaseLinkRoute<TContext, TConditionName> | null;
    next: BaseLinkRoute<TContext, TConditionName> | null;
    current: BaseLinkRoute<TContext, TConditionName> | null;
};
